import { useState } from 'react';
import { Clock, CheckCircle, XCircle, Edit3, AlertCircle, FileText, Calendar, User, Building2, ArrowLeft } from 'lucide-react';
import { useProposedRecords, ProposedRecord } from '../../context/ProposedRecordsContext';
import { ReviewProposalModal } from './ReviewProposalModal';

interface PendingApprovalsProps {
  petId: string;
  petName: string;
  onClose: () => void;
}

export function PendingApprovals({ petId, petName, onClose }: PendingApprovalsProps) {
  const { getProposedRecordsForPet } = useProposedRecords();
  const [selectedRecord, setSelectedRecord] = useState<ProposedRecord | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const proposedRecords = getProposedRecordsForPet(petId).filter(r => r.status === 'proposed');
  const approvedRecords = getProposedRecordsForPet(petId).filter(r => r.status === 'approved');
  const rejectedRecords = getProposedRecordsForPet(petId).filter(r => r.status === 'rejected');

  const getRecordIcon = (type: string) => {
    switch (type) {
      case 'vaccine':
        return '💉';
      case 'grooming':
        return '✂️';
      case 'treatment':
        return '🏥';
      case 'document':
        return '📄';
      case 'medical':
        return '⚕️';
      case 'note':
        return '📝';
      default:
        return '📋';
    }
  };

  const handleReviewClick = (record: ProposedRecord) => {
    setSelectedRecord(record);
    setShowReviewModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Profile
          </button>
          <h1 className="text-3xl mb-2">Pending Approvals</h1>
          <p className="text-gray-600">Review and approve updates proposed by professionals for {petName}</p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-100 border border-blue-300 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="mb-1">You are in control of {petName}'s records.</p>
            <p>All proposed updates require your approval before they become part of the official pet profile. Once approved, all authorized users will see the updates in real-time.</p>
          </div>
        </div>

        {/* Proposed Records (Pending Review) */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-orange-600" />
              <h2 className="text-xl">Pending Your Review</h2>
              {proposedRecords.length > 0 && (
                <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full text-sm">
                  {proposedRecords.length}
                </span>
              )}
            </div>
          </div>

          {proposedRecords.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No pending proposals</p>
              <p className="text-sm mt-1">When professionals propose updates, they'll appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {proposedRecords.map(record => (
                <div
                  key={record.id}
                  className="border border-orange-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-orange-50/30"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3 flex-1">
                      <div className="text-3xl">{getRecordIcon(record.recordType)}</div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium capitalize">{record.recordType} - {record.data.vaccineName || record.data.serviceName || record.data.treatmentName || 'Update'}</h3>
                          <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">
                            Pending Review
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            <span>Proposed by {record.professionalName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span>{record.professionalBusinessName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Submitted {new Date(record.submittedDate).toLocaleDateString()}</span>
                          </div>
                          {record.data.recommendedDate && (
                            <div className="flex items-center gap-2 text-blue-600">
                              <Calendar className="w-4 h-4" />
                              <span>Recommended for {new Date(record.data.recommendedDate).toLocaleDateString()}</span>
                            </div>
                          )}
                        </div>
                        {record.data.notes && (
                          <p className="text-sm text-gray-700 mt-2 bg-white p-2 rounded border border-gray-200">
                            {record.data.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleReviewClick(record)}
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity whitespace-nowrap ml-3"
                    >
                      Review
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recently Approved */}
        {approvedRecords.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <h2 className="text-xl">Recently Approved</h2>
              <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
                {approvedRecords.length}
              </span>
            </div>
            <div className="space-y-3">
              {approvedRecords.slice(0, 5).map(record => (
                <div
                  key={record.id}
                  className="border border-green-200 rounded-lg p-4 bg-green-50/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-3xl">{getRecordIcon(record.recordType)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium capitalize">{record.recordType} - {record.data.vaccineName || record.data.serviceName || record.data.treatmentName || 'Update'}</h3>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <div className="text-sm text-gray-600">
                        <span>By {record.professionalName} • </span>
                        <span>Approved {record.reviewedDate && new Date(record.reviewedDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recently Rejected */}
        {rejectedRecords.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="w-5 h-5 text-red-600" />
              <h2 className="text-xl">Recently Rejected</h2>
              <span className="bg-red-100 text-red-700 px-2 py-1 rounded-full text-sm">
                {rejectedRecords.length}
              </span>
            </div>
            <div className="space-y-3">
              {rejectedRecords.slice(0, 3).map(record => (
                <div
                  key={record.id}
                  className="border border-red-200 rounded-lg p-4 bg-red-50/30"
                >
                  <div className="flex items-start gap-3">
                    <div className="text-3xl opacity-50">{getRecordIcon(record.recordType)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium capitalize">{record.recordType} - {record.data.vaccineName || record.data.serviceName || record.data.treatmentName || 'Update'}</h3>
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="text-sm text-gray-600 mb-2">
                        <span>By {record.professionalName} • </span>
                        <span>Rejected {record.reviewedDate && new Date(record.reviewedDate).toLocaleDateString()}</span>
                      </div>
                      {record.rejectionReason && (
                        <p className="text-sm text-red-700 bg-white p-2 rounded border border-red-200">
                          Reason: {record.rejectionReason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Review Modal */}
      {showReviewModal && selectedRecord && (
        <ReviewProposalModal
          record={selectedRecord}
          onClose={() => {
            setShowReviewModal(false);
            setSelectedRecord(null);
          }}
        />
      )}
    </div>
  );
}
